import { useCallback, useEffect, useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { DragEndEvent, DragStartEvent } from '@dnd-kit/core';
import {
  coachSessionsEndpoints,
  ICoachSession,
  ICoachSessionListItem,
  IMoveOrDuplicateBody,
} from '../../endpoints/coachSessions';

/**
 * Hook drag-and-drop calendario settimanale/mensile.
 * Spec v1.1.1 sez 2 righe 150-155:
 *  - drag card tra giorni → sposta (conferma se published)
 *  - Shift + drag → duplica invece di spostare
 */
export function useWeekDnd(
  teamId: string,
  sessions: ICoachSessionListItem[],
) {
  const queryClient = useQueryClient();
  const shiftDownRef = useRef(false);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Shift') shiftDownRef.current = true;
    };
    const onKeyUp = (e: KeyboardEvent) => {
      if (e.key === 'Shift') shiftDownRef.current = false;
    };
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
    };
  }, []);

  const invalidate = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['coachSessions', teamId] });
  }, [queryClient, teamId]);

  const moveMut = useMutation({
    mutationFn: ({ id, body }: { id: string; body: IMoveOrDuplicateBody }) =>
      coachSessionsEndpoints.move(id, body).then((r) => r.data),
    onSuccess: invalidate,
  });

  const duplicateMut = useMutation({
    mutationFn: ({ id, body }: { id: string; body: IMoveOrDuplicateBody }) =>
      coachSessionsEndpoints.duplicate(id, body).then((r) => r.data),
    onSuccess: invalidate,
  });

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const sessionId = String(event.active.id);
      const targetDate = event.over?.id ? String(event.over.id) : null;
      if (!targetDate) return;

      const source = sessions.find((s) => s._id === sessionId);
      if (!source) return;

      const sourceDate = source.scheduledDate.slice(0, 10);
      if (sourceDate === targetDate) return;

      const body: IMoveOrDuplicateBody = {
        targetDate,
        targetTime: source.startTime,
      };

      if (shiftDownRef.current) {
        duplicateMut.mutate({ id: sessionId, body });
        return;
      }

      if (source.status === 'published') {
        const ok = window.confirm(
          `Spostare la sessione "${source.title}" pubblicata dal ${sourceDate} al ${targetDate}?`,
        );
        if (!ok) return;
      }
      if (source.status === 'executed') {
        window.alert('Non è possibile spostare una sessione eseguita.');
        return;
      }

      moveMut.mutate({ id: sessionId, body });
    },
    [sessions, moveMut, duplicateMut],
  );

  const handleDragStart = useCallback((_event: DragStartEvent) => {
    // placeholder per eventuali side effect visivi (overlay, ecc.)
  }, []);

  return {
    handleDragEnd,
    handleDragStart,
    isMoving: moveMut.isPending,
    isDuplicating: duplicateMut.isPending,
    duplicateSession: (session: ICoachSessionListItem | ICoachSession, targetDate: string, targetTime?: string) =>
      duplicateMut.mutate({
        id: session._id,
        body: { targetDate, targetTime: targetTime ?? (session as any).startTime },
      }),
    moveSession: (session: ICoachSessionListItem | ICoachSession, targetDate: string, targetTime?: string) =>
      moveMut.mutate({
        id: session._id,
        body: { targetDate, targetTime: targetTime ?? (session as any).startTime },
      }),
  };
}
